import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, KeyRound } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ForgotPassword() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState(params.get("email") || "");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!email.trim()) {
            setErr("Please enter your email.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            nav("/reset-password", { state: { email: email.trim().toLowerCase() } });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-navy-900 grid-pattern px-6 py-12">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-28 h-28 flex items-center justify-center">
                        <img src="/gpm-logo.png" alt="GPM" className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.5)]" />
                    </div>
                    <div className="text-left">
                        <div className="font-heading font-bold text-white text-xl leading-tight">Global Property Mart</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold-500/90">One Platform · Every Need</div>
                    </div>
                </Link>

                <div className="rounded-3xl glass p-8 gold-border-glow" data-testid="forgot-password-card">
                    <div className="flex items-center gap-2 text-gold-500">
                        <KeyRound className="w-5 h-5" />
                        <span className="text-[11px] tracking-widest uppercase font-bold">Forgot Password</span>
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-white mt-2">Reset your password</h1>
                    <p className="text-white/65 text-sm mt-2">
                        Enter the email you signed up with. We'll send you a 4-digit code to reset your password.
                    </p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="forgot-password-form">
                        <div>
                            <label className="text-xs text-white/60 mb-1 block flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> Email
                            </label>
                            <input
                                required type="email"
                                data-testid="forgot-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white"
                                placeholder="you@example.com"
                            />
                        </div>

                        {err && <div className="text-sm text-red-400 text-center" data-testid="forgot-password-error">{err}</div>}

                        <button
                            type="submit" disabled={submitting}
                            data-testid="forgot-password-submit"
                            className="w-full px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-60"
                        >{submitting ? "Sending code…" : "Send reset code"}</button>
                    </form>

                    <div className="mt-6 text-center text-sm text-white/65 space-y-2">
                        <div>
                            Remember it? <Link to="/login" className="text-gold-500 font-medium">Sign in</Link>
                        </div>
                        <div className="text-xs text-white/45">
                            Already have a code? <Link to="/reset-password" className="text-gold-500/80 hover:text-gold-500">Enter it here</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
